import { useCallback } from 'react'

import { signPersonalSignText } from '@/lib/lighter/sign-subaccount-key'
import type { SelectionState } from '@/lib/lighter/subaccount-selection'
import {
  lighterConfirmSubaccountKey,
  lighterLinkSubaccounts,
  lighterListSubaccounts,
  lighterPrepareSubaccountKey,
} from '@/service/venue.api'
import type {
  LighterConfirmSubaccountKeyResponse,
  LighterLinkSubaccountsResponse,
  LighterListSubaccountsResponse,
  LighterPrepareSubaccountKeyResponse,
  LighterSubaccountLink,
} from '@/types/generated/public_api'

export function useLighterOnboard() {
  const listSubaccounts = useCallback(
    (accountId: string, l1Address: string): Promise<LighterListSubaccountsResponse> =>
      lighterListSubaccounts({ accountId, l1Address }),
    []
  )

  const prepareKey = useCallback(
    (
      accountId: string,
      l1Address: string,
      accountIndex: string,
      apiKeyIndex: number
    ): Promise<LighterPrepareSubaccountKeyResponse> =>
      lighterPrepareSubaccountKey({ accountId, l1Address, accountIndex, apiKeyIndex }),
    []
  )

  // Signs `personalSignText` with the connected wallet and confirms in one
  // call — the picker's "mint via wallet" action for a single slot.
  const signAndConfirm = useCallback(
    async (
      walletAddress: string,
      preparedKeyId: string,
      personalSignText: string
    ): Promise<LighterConfirmSubaccountKeyResponse> => {
      const personalSignSignature = await signPersonalSignText({ walletAddress, personalSignText })
      return lighterConfirmSubaccountKey({ preparedKeyId, personalSignSignature })
    },
    []
  )

  // Resolves the picker's selection state down to the wire's `links` array
  // and submits the batch. A slot whose key source isn't resolved is a
  // caller bug (the picker's canSubmit gate should have prevented this) —
  // thrown synchronously rather than silently dropped, so it fails loudly
  // in development instead of silently omitting a slot the user expected
  // to submit.
  const linkSubaccounts = useCallback(
    (
      accountId: string,
      l1Address: string,
      selection: SelectionState
    ): Promise<LighterLinkSubaccountsResponse> => {
      const links: LighterSubaccountLink[] = selection.slots.map((slot) => {
        if (slot.keySource.type === 'confirmed') {
          return {
            accountIndex: slot.accountIndex,
            apiKeyIndex: slot.apiKeyIndex,
            preparedKeyId: slot.keySource.preparedKeyId,
          }
        }
        throw new Error(
          `lighter: slot ${slot.accountIndex} has no resolved key source (${slot.keySource.type}) — canSubmit should have blocked this`
        )
      })
      return lighterLinkSubaccounts({ accountId, l1Address, links })
    },
    []
  )

  return { listSubaccounts, prepareKey, signAndConfirm, linkSubaccounts }
}
